import { useId, useState, type DragEvent } from 'react';
import { FileArchiveIcon, FileTextIcon, UploadIcon } from 'lucide-react';

import { Button } from '@/components/ui/button';
import { cn } from '@/lib/utils';

/**
 * Where a workflow file or a bundle goes in (spec 208).
 *
 * A bare "Choose File" hid that a bundle is taken at all, and once a file was
 * chosen it shrank to one line of grey text. So the zone says what it takes,
 * and a chosen file becomes a chip that says what it is, with Replace.
 *
 * The native file input stays, visually hidden inside the zone's label: browse
 * and the keyboard use the platform's own picker, and the input keeps the name
 * "Workflow file" that the upload's tests and walk-throughs address. Dropping
 * adds what the input cannot: the first dropped file goes the same way.
 */
export function FileDrop({
  file,
  describe,
  disabled = false,
  invalid = false,
  describedBy,
  onFile,
}: {
  file: File | null;
  /** What the chip says, e.g. "Bundle · Epic delivery · 9 items". */
  describe: string;
  disabled?: boolean;
  invalid?: boolean;
  describedBy?: string;
  /** A file chosen or dropped; `null` on Replace. */
  onFile: (file: File | null) => void;
}) {
  const noteId = useId();
  const [dragging, setDragging] = useState(false);
  const [extra, setExtra] = useState(false);

  const note = extra ? (
    <p id={noteId} className="text-muted-foreground text-xs">
      Only the first file was taken.
    </p>
  ) : null;

  if (file) {
    const Icon = /\.zip$/i.test(file.name) ? FileArchiveIcon : FileTextIcon;
    return (
      <div className="flex flex-col gap-1">
        <div className="border-border bg-muted/30 flex min-h-11 items-center gap-2 rounded-lg border px-3 py-1.5">
          <Icon
            aria-hidden="true"
            className="text-muted-foreground size-4 shrink-0"
          />
          <span className="min-w-0 flex-1 truncate text-sm">{describe}</span>
          <Button
            variant="ghost"
            size="sm"
            disabled={disabled}
            onClick={() => {
              setExtra(false);
              onFile(null);
            }}
          >
            Replace
          </Button>
        </div>
        {note}
      </div>
    );
  }

  const take = (event: DragEvent<HTMLLabelElement>) => {
    event.preventDefault();
    setDragging(false);
    if (disabled) return;
    const dropped = event.dataTransfer.files;
    const first = dropped[0];
    if (!first) return;
    setExtra(dropped.length > 1);
    onFile(first);
  };

  return (
    <div className="flex flex-col gap-1">
      <label
        className={cn(
          'border-border hover:bg-muted/40 has-focus-visible:ring-ring/50 flex min-h-20 cursor-pointer flex-col items-center justify-center gap-1 rounded-lg border border-dashed px-4 py-3 text-center text-sm has-focus-visible:ring-3',
          dragging && 'border-primary bg-primary/5',
          disabled && 'cursor-default opacity-60',
        )}
        onDragOver={(event) => {
          event.preventDefault();
          if (!disabled) setDragging(true);
        }}
        onDragLeave={() => setDragging(false)}
        onDrop={take}
      >
        <UploadIcon
          aria-hidden="true"
          className="text-muted-foreground size-4"
        />
        <span>
          Drop a .yaml or a .zip bundle here, or{' '}
          <span className="text-primary underline underline-offset-3">
            browse
          </span>
        </span>
        <input
          type="file"
          accept=".yaml,.yml,.zip"
          aria-label="Workflow file"
          aria-invalid={invalid}
          aria-describedby={
            [describedBy, extra ? noteId : undefined]
              .filter(Boolean)
              .join(' ') || undefined
          }
          className="sr-only"
          disabled={disabled}
          onChange={(event) => {
            setExtra(false);
            onFile(event.target.files?.[0] ?? null);
          }}
        />
      </label>
      {note}
    </div>
  );
}
